/**
 * Разбор текста задания на читаемые части.
 *
 * Бэкенд отдаёт задание одной строкой, в которой разметка живёт внутри текста:
 * блоки помечены номером, вопросы внутри блока — звёздочками, перечисления —
 * тире. Сплошным абзацем это читается плохо: на телефоне вопросы сливаются со
 * вступлением, и человек теряет, на что именно отвечает.
 *
 * Здесь текст только разбирается; как показывать части, решает компонент.
 */

export type TaskBlock =
  /** Вступление или пояснение между блоками. */
  | { kind: 'text'; text: string }
  /** Пронумерованный блок с вопросами внутри. */
  | { kind: 'step'; number: string; title: string; items: string[] }
  /** Перечисление после строки-заголовка с двоеточием. */
  | { kind: 'list'; title: string | null; items: string[] }
  /** Определение: короткий термин, точка и объяснение. */
  | { kind: 'term'; title: string; text: string };

/** Строка вида «1. Мой смысл * Вопрос * Вопрос». */
const STEP = /^(\d{1,2})[.)]\s*(.+)$/;

/** Перечисление через тире: «— первое; — второе». */
const DASH_ITEM = /^[—–-]\s*/;

/**
 * Определение: «Базовое доверие. Пациент проверяет…».
 *
 * Термин ограничен тремя словами намеренно. Обычное предложение тоже
 * начинается с заглавной буквы и кончается точкой, и без такого ограничения
 * первая фраза любого абзаца превращалась бы в заголовок.
 */
const TERM = /^([А-ЯЁA-Z][^.!?]{2,48})\.\s+(\S.+)$/;

export function parseTaskText(source: string): TaskBlock[] {
  const blocks: TaskBlock[] = [];

  const lines = (source ?? '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

  for (const [index, line] of lines.entries()) {
    // Слово «Задание» первой строкой дублирует заголовок экрана
    if (index === 0 && /^задание$/i.test(line)) continue;

    if (DASH_ITEM.test(line)) {
      const items = splitDashItems(line);
      const previous = blocks[blocks.length - 1];

      // Тире продолжают предыдущий заголовок с двоеточием
      if (previous?.kind === 'list' && previous.items.length === 0) previous.items.push(...items);
      else blocks.push({ kind: 'list', title: null, items });
      continue;
    }

    const step = STEP.exec(line);
    if (step) {
      const [title, ...items] = splitStars(step[2]);
      blocks.push({ kind: 'step', number: step[1], title, items });
      continue;
    }

    if (line.includes('*')) {
      const [title, ...items] = splitStars(line);
      if (items.length > 0) {
        blocks.push({ kind: 'list', title: title || null, items });
        continue;
      }
    }

    // Двоеточие в конце обещает перечисление: оно придёт следующей строкой
    if (line.endsWith(':') && !line.startsWith('«')) {
      blocks.push({ kind: 'list', title: line, items: [] });
      continue;
    }

    const term = TERM.exec(line);
    if (term && term[1].split(/\s+/).length <= 3) {
      blocks.push({ kind: 'term', title: term[1], text: term[2] });
      continue;
    }

    blocks.push({ kind: 'text', text: line });
  }

  // Заголовок без перечисления — обычный абзац: пустой список выглядел бы
  // как потерянные данные
  return blocks.map((block) =>
    block.kind === 'list' && block.items.length === 0 && block.title
      ? { kind: 'text' as const, text: block.title }
      : block
  );
}

/** «Заголовок * вопрос * вопрос» → части без звёздочек. */
function splitStars(text: string): string[] {
  return text
    .split('*')
    .map((part) => part.trim())
    .filter(Boolean);
}

/** «— первое; — второе; — третье» → отдельные пункты. */
function splitDashItems(line: string): string[] {
  return line
    .split(/;\s*(?=[—–-]\s)/)
    .map((part) => part.replace(DASH_ITEM, '').replace(/;$/, '').trim())
    .filter(Boolean);
}
